import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { 
  FileText, 
  ArrowLeft,
  Phone,
  Mail,
  Building2,
  Calendar,
  Hash,
  Package,
  Loader2,
  Printer
} from 'lucide-react';
import { InBooks24Logo } from './InBooks24Logo';
import { grnApiService, GRN, GRNDetail } from '../services/grnApi';
import { Branch, branchApiService } from '../services/branchApi';
import { toast } from 'sonner';

export function GRNViewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [grn, setGrn] = useState<GRN | null>(null);
  const [grnDetails, setGrnDetails] = useState<GRNDetail[]>([]);
  const [branch, setBranch] = useState<Branch | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      loadGRNData();
    }
  }, [id]);

  const loadGRNData = async () => {
    try {
      setLoading(true);
      const [grnData, detailsData] = await Promise.all([
        grnApiService.getGRNById(id!),
        grnApiService.getGRNDetailsByGRN(parseInt(id!))
      ]);
      
      setGrn(grnData);
      setGrnDetails(detailsData);
      
      // Load branch information if available
      if (grnData.branchId) {
        try {
          const branches = await branchApiService.getBranches();
          const branchData = branches.find(b => b.id === grnData.branchId);
          if (branchData) {
            setBranch(branchData);
          }
        } catch (error) {
          // console.warn('Could not load branch information:', error);
        }
      }
    } catch (error) {
      // console.error('Error loading GRN data:', error);
      toast.error('Failed to load GRN details');
      navigate('/grn');
    } finally {
      setLoading(false);
    }
  };


  const handlePrint = () => {
    window.print();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit'
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'Completed':
        return <Badge className="bg-green-100 text-green-800 border-green-200">Completed</Badge>;
      case 'Pending':
        return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">Pending</Badge>;
      case 'Cancelled':
        return <Badge className="bg-red-100 text-red-800 border-red-200">Cancelled</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
        <span className="ml-2 text-gray-600">Loading GRN details...</span>
      </div>
    );
  }

  if (!grn) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <FileText className="h-12 w-12 mx-auto text-gray-400 mb-4" />
          <p className="text-lg font-medium text-gray-600">GRN not found</p>
          <Button 
            onClick={() => navigate('/grn')}
            className="mt-4 bg-primary-500 hover:bg-primary-600 text-white"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to GRN List
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-100/50 p-4 print:p-0 print:bg-white print:min-h-0">
      <div className="max-w-6xl mx-auto print:max-w-full print:m-0">
        {/* Document Container */}
        <Card className="border-primary-200 shadow-2xl shadow-primary-100/50 grn-pdf print:shadow-none print:border-none print:w-full print:m-0">
          <CardContent className="p-0 print:p-2 print:w-full">
            {/* Header Section */}
            <div className="bg-white p-8 border-b border-primary-200 print:p-2 print:mb-4">
              <div className="flex justify-between items-start mb-6 print:mb-4">
                {/* Logo */}
                <div className="flex items-center gap-4 print:hidden">
                  <InBooks24Logo size="lg" variant="horizontal" />
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 print:hidden">
                  <Button
                    onClick={handlePrint}
                    variant="outline"
                    size="sm"
                    className="border-gray-300 text-gray-700 hover:bg-gray-50"
                  >
                    <Printer className="h-4 w-4 mr-2" />
                    Print
                  </Button>
                </div>
              </div>

              {/* Title */}
              <div className="text-center mb-8 print:mb-4">
                <h1 className="text-3xl font-bold text-gray-900 mb-2 print:text-2xl print:mb-1">Goods Received Note</h1>
              </div>

              {/* GRN Details */}
              <div className="flex justify-center mb-6 print:mb-4">
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 print:p-2">
                  <div className="grid grid-cols-2 gap-4 text-sm print:gap-2 print:text-xs">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Hash className="h-4 w-4 text-gray-500" />
                        <span className="font-medium text-gray-600">GRN No.:</span>
                        <span className="font-bold text-gray-900">{grn.grnNumber}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-gray-500" />
                        <span className="font-medium text-gray-600">PO No.:</span>
                        <span className="font-bold text-gray-900">{grn.poHeader?.poNumber || 'N/A'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-gray-500" />
                        <span className="font-medium text-gray-600">DC No.:</span>
                        <span className="font-bold text-gray-900">{grn.dcNo || 'N/A'}</span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-gray-500" />
                        <span className="font-medium text-gray-600">Received Date:</span>
                        <span className="font-bold text-gray-900">{formatDate(grn.receiveDate)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-gray-500" />
                        <span className="font-medium text-gray-600">GRN Create Date:</span>
                        <span className="font-bold text-gray-900">{formatDate(grn.grnDate)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-gray-500" />
                        <span className="font-medium text-gray-600">DC Date:</span>
                        <span className="font-bold text-gray-900">{grn.dcDate ? formatDate(grn.dcDate) : 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Company and Supplier Information */}
            <div className="bg-white p-8 border-b border-primary-200 print:p-2 print:mb-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 print:grid-cols-2 print:gap-4">
                {/* Received By Section */}
                <div className="bg-gradient-to-r from-primary-50 to-primary-100/50 p-6 rounded-lg border border-primary-200">
                  <h3 className="text-lg font-bold text-primary-800 mb-4 flex items-center gap-2">
                    <FileText className="h-5 w-5 text-primary-700" />
                    Received By
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <p className="font-bold text-gray-900 text-lg">
                        {branch?.branchName || 'INBOOKS24 MANUFACTURING'}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-700">
                        {branch?.address || 'Address: 123 Manufacturing Street, Industrial Area, City - 123456'}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-700">GSTIN: {branch?.gstin || '12ABCDE1234F1Z5'}</p>
                    </div>
                    {branch?.panNo && (
                      <div>
                        <p className="text-gray-700">PAN: {branch.panNo}</p>
                      </div>
                    )}
                    <div className="flex items-center gap-4 mt-4">
                      {branch?.phone && (
                        <div className="flex items-center gap-2">
                          <Phone className="h-4 w-4 text-gray-500" />
                          <span className="text-gray-700">{branch.phone}</span>
                        </div>
                      )}
                      {branch?.email && (
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-gray-500" />
                          <span className="text-gray-700">{branch.email}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Supplied By Section */}
                {grn.supplier && (
                  <div className="bg-gradient-to-r from-primary-50 to-primary-100/50 p-6 rounded-lg border border-primary-200">
                    <h3 className="text-lg font-bold text-primary-800 mb-4 flex items-center gap-2">
                      <FileText className="h-5 w-5 text-primary-700" />
                      Supplied By
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <p className="font-bold text-gray-900 text-lg">{grn.supplier.supplierName}</p>
                      </div>
                      <div>
                        <p className="text-gray-700">
                          {grn.supplier.billAddress || grn.supplier.shipAddress || 'Address not available'}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-700">GSTIN: {grn.supplier.gstNo || 'N/A'}</p>
                      </div>
                      {grn.supplier.panNo && (
                        <div>
                          <p className="text-gray-700">PAN: {grn.supplier.panNo}</p>
                        </div>
                      )}
                      <div className="flex items-center gap-4 mt-4">
                        {grn.supplier.contactNo && (
                          <div className="flex items-center gap-2">
                            <Phone className="h-4 w-4 text-gray-500" />
                            <span className="text-gray-700">{grn.supplier.contactNo}</span>
                          </div>
                        )}
                        {grn.supplier.email && (
                          <div className="flex items-center gap-2">
                            <Mail className="h-4 w-4 text-gray-500" />
                            <span className="text-gray-700">{grn.supplier.email}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Items Table */}
            <div className="bg-white p-8 print:p-2">
              <div className="mb-6 print:mb-4">
                <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2 print:text-sm print:mb-1">
                  <Package className="h-5 w-5 print:h-4 print:w-4" />
                  Item Details
                </h3>
                <p className="text-gray-600 print:text-xs">Received items and quantities</p>
              </div>

              {grnDetails.length > 0 ? (
                <div className="border border-gray-200 rounded-lg overflow-hidden print:overflow-visible print:border-gray-400">
                  <Table className="print:text-[9px]">
                    <TableHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 print:bg-gray-100">
                      <TableRow className="border-primary-200">
                        <TableHead className="text-left font-semibold text-gray-700 print:text-[9px] print:py-1 print:px-1">Category</TableHead>
                        <TableHead className="text-center font-semibold text-gray-700 print:text-[9px] print:py-1 print:px-1">Heat No</TableHead>
                        <TableHead className="text-left font-semibold text-gray-700 print:text-[9px] print:py-1 print:px-1">Item</TableHead>
                        <TableHead className="text-center font-semibold text-gray-700 print:text-[9px] print:py-1 print:px-1">Supplier Heat No</TableHead>
                        <TableHead className="text-center font-semibold text-gray-700 print:text-[9px] print:py-1 print:px-1">NOP</TableHead>
                        {grn.type !== 'MANUAL' && (
                          <>
                            <TableHead className="text-center font-semibold text-gray-700 print:text-[9px] print:py-1 print:px-1">Rate</TableHead>
                            <TableHead className="text-center font-semibold text-gray-700 print:text-[9px] print:py-1 print:px-1">PO Qty</TableHead>
                            <TableHead className="text-center font-semibold text-gray-700 print:text-[9px] print:py-1 print:px-1">Remaining Qty</TableHead>
                            <TableHead className="text-center font-semibold text-gray-700 print:text-[9px] print:py-1 print:px-1">Total</TableHead>
                          </>
                        )}
                        <TableHead className="text-center font-semibold text-gray-700 print:text-[9px] print:py-1 print:px-1">Received Qty</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {grnDetails.map((detail, index) => (
                        <TableRow key={detail.id} className={`border-gray-100 hover:bg-gray-50 print:text-[9px] ${index % 2 === 0 ? 'print:bg-gray-50' : 'print:bg-white'}`}>
                          <TableCell className="font-medium text-gray-900 print:py-1 print:px-1">
                            {detail.rawItem?.categoryName || 'RAW MATERIAL'}
                          </TableCell>
                          <TableCell className="text-center text-gray-700 print:py-1 print:px-1">
                            {detail.heatNo || 'N/A'}
                          </TableCell>
                          <TableCell className="text-gray-700 print:py-1 print:px-1">
                            {detail.rawItem?.itemName || 'N/A'}
                          </TableCell>
                          <TableCell className="text-center text-gray-700 print:py-1 print:px-1">
                            {detail.supplierHeatNo || 'N/A'}
                          </TableCell>
                          <TableCell className="text-center text-gray-700 print:py-1 print:px-1">
                            {detail.nop || 'N/A'}
                          </TableCell>
                          {grn.type !== 'MANUAL' && (
                            <>
                              <TableCell className="text-center text-gray-700 print:py-1 print:px-1">
                                ₹{detail.unitPrice?.toLocaleString() || '0'}
                              </TableCell>
                              <TableCell className="text-center text-gray-700 print:py-1 print:px-1">
                                {detail.quantity?.toFixed(2) || '0.00'}
                              </TableCell>
                              <TableCell className="text-center text-gray-700 print:py-1 print:px-1">
                                {(detail.quantity - detail.receivedQuantity)?.toFixed(2) || '0.00'}
                              </TableCell>
                              <TableCell className="text-center font-semibold text-gray-900 print:py-1 print:px-1">
                                ₹{detail.lineTotal?.toLocaleString() || '0'}
                              </TableCell>
                            </>
                          )}
                          <TableCell className="text-center font-semibold text-gray-900 print:py-1 print:px-1">
                            {detail.receivedQuantity?.toFixed(2) || '0.00'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Package className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-lg font-medium mb-2">No Items Found</p>
                  <p className="text-sm">No items have been added to this GRN yet.</p>
                </div>
              )}

              {/* Summary */}
              {grnDetails.length > 0 && (
                <div className="mt-6 flex justify-end print:mt-4">
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 print:p-2">
                    <div className={`grid gap-6 text-sm print:gap-2 print:text-xs ${grn.type === 'MANUAL' ? 'grid-cols-1' : 'grid-cols-2'}`}>
                      <div className="text-right">
                        <span className="text-gray-600">Total Quantity:</span>
                        <span className="ml-2 font-bold text-gray-900">
                          {grnDetails.reduce((sum, detail) => sum + (detail.receivedQuantity || 0), 0).toFixed(2)}
                        </span>
                      </div>
                      {grn.type !== 'MANUAL' && (
                        <div className="text-right">
                          <span className="text-gray-600">Total Amount:</span>
                          <span className="ml-2 font-bold text-gray-900">
                            ₹{grn.totalAmount?.toLocaleString() || '0'}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="bg-gray-50 p-6 border-t border-gray-200 print:p-2 print:mt-4">
              <div className="flex justify-between items-center print:text-xs">
                <div className="text-sm text-gray-600">
                  <p>Status: {getStatusBadge(grn.status)}</p>
                  <p className="mt-1">Received By: {grn.receivedBy}</p>
                </div>
                <div className="text-sm text-gray-600">
                  <p>Created: {formatDate(grn.createdAt)}</p>
                  <p className="mt-1">Last Updated: {formatDate(grn.updatedAt)}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Back Button */}
        <div className="mt-6 flex justify-start print:hidden">
          <Button
            onClick={() => navigate('/grn')}
            variant="outline"
            className="border-primary-200 text-primary-700 hover:bg-primary-50"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to GRN List
          </Button>
        </div>
      </div>
    </div>
  );
}
